import React, { useState, useEffect, useRef, useContext, useCallback } from "react";
import PropTypes from "prop-types";
import { useMsal } from "@azure/msal-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import SearchIcon from "@mui/icons-material/Search";
import { CarrierService } from "../../../../services/CarrierService";
import { AlertContext } from "../../../Dashboard/Dashboard";
import { formatTitleCase } from "../../../../utils/formatHelpers";

export default function CarrierSearchSwap({ carrier }) {
  const { instance } = useMsal();
  const { warning } = useContext(AlertContext);
  const navigate = useNavigate();
  let [searchParams] = useSearchParams();

  const [isSearching, setIsSearching] = useState(false);
  const [options, setOptions] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const searchRef = useRef(null);
  const debounceRef = useRef(null);

  // When carrier changes, reset to display mode
  useEffect(() => {
    setIsSearching(false);
    setSearchInput("");
    setOptions([]);
  }, [carrier]);

  // Auto-focus the input when switching to search mode
  useEffect(() => {
    if (isSearching && searchRef.current) {
      setTimeout(() => {
        if (searchRef.current) {
          searchRef.current.focus();
        }
      }, 50);
    }
  }, [isSearching]);

  // Debounced carrier search
  const handleSearch = useCallback((value) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    if (!value || value.length < 2) {
      setOptions([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    debounceRef.current = setTimeout(() => {
      const params = { carrierName: value };
      CarrierService.FindCarrier(instance, params)
        .then((res) => {
          setOptions(res || []);
          setIsLoading(false);
        })
        .catch((error) => {
          console.error(error);
          setIsLoading(false);
          warning(error);
        });
    }, 300);
  }, [instance, warning]);

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  // Navigate to selected carrier's profile
  const handleCarrierSelect = (event, newValue) => {
    if (newValue && newValue.id) {
      const currentTab = searchParams.get("tab");
      const tabParam = currentTab ? `&tab=${currentTab}` : "";
      setIsSearching(false);
      setOptions([]);
      setSearchInput("");
      navigate(`/dashboard/carrier-profile?carrierId=${newValue.id}${tabParam}`);
    }
  };

  // On blur without selection, revert to display mode
  const handleBlur = () => {
    setTimeout(() => {
      setIsSearching(false);
      setSearchInput("");
      setOptions([]);
    }, 200);
  };

  // Click on the heading to switch to search mode
  const handleHeadingClick = () => {
    setIsSearching(true);
  };

  // ── DISPLAY MODE: Plain heading with search icon ──
  if (!isSearching) {
    return (
      <Box
        onClick={handleHeadingClick}
        sx={{
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
        }}
      >
        <Typography
          className="carrier-heading-text"
          variant="h1"
          color="black_opacity.100"
          sx={{
            borderBottom: "2px dashed",
            borderColor: "black_opacity.30",
            transition: "border-color 0.2s",
          }}
        >
          {formatTitleCase(carrier.name)}
        </Typography>
        <SearchIcon
          className="search-hint-icon"
          sx={{
            fontSize: "20px",
            color: "black_opacity.40",
            marginLeft: "8px",
            opacity: 1,
            transition: "opacity 0.2s",
          }}
        />
      </Box>
    );
  }

  // ── SEARCH MODE: Autocomplete input ──
  return (
    <Autocomplete
      freeSolo
      autoHighlight
      openOnFocus
      options={options}
      filterOptions={(x) => x}
      inputValue={searchInput}
      onChange={handleCarrierSelect}
      onInputChange={(event, newInputValue, reason) => {
        if (reason === "input") {
          setSearchInput(newInputValue);
          handleSearch(newInputValue);
        }
        if (reason === "clear") {
          setSearchInput("");
          setOptions([]);
          setIsLoading(false);
        }
      }}
      onBlur={handleBlur}
      getOptionLabel={(option) =>
        option && option.id ? `${option.id} - ${formatTitleCase(option.name)}` : ""
      }
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.id}>
          <Box>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {option.id} - {formatTitleCase(option.name)}
            </Typography>
            {option.mcNumber && (
              <Typography variant="caption" color="black_opacity.70">
                MC {option.mcNumber}
                {option.city && ` • ${formatTitleCase(option.city)}, ${option.state}`}
              </Typography>
            )}
          </Box>
        </Box>
      )}
      PaperComponent={({ children }) => (
        <Paper
          sx={{
            backgroundColor: "black_solid.10",
            fontSize: "body1.fontSize",
            fontWeight: "body1.fontWeight",
            boxShadow: "elevation1",
          }}
        >
          {children}
        </Paper>
      )}
      sx={{
        width: "auto",
        minWidth: "350px",
        maxWidth: "500px",
        "& .MuiOutlinedInput-root": {
          padding: "2px 8px !important",
        },
        "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
          border: "2px solid",
          borderColor: "primary.60",
          borderRadius: "4px",
        },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          inputRef={searchRef}
          placeholder="Type carrier name or ID..."
          variant="outlined"
          size="small"
          autoFocus
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <SearchIcon sx={{ fontSize: "20px", color: "black_opacity.40", marginRight: "4px" }} />
            ),
          endAdornment: (
              <>
                {isLoading && (
                  <CircularProgress color="inherit" size={18} sx={{ marginRight: "28px" }} />
                )}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
          inputProps={{
            ...params.inputProps,
            style: {
              fontSize: "0.95rem",
              fontWeight: 500,
              padding: "4px 4px",
            },
          }}
        />
      )}
    />
  );
}

CarrierSearchSwap.propTypes = {
  carrier: PropTypes.object.isRequired,
};